import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Building, Building2, MapPin, Save, X, Info, ImagePlus, Hash, Phone, Mail } from "lucide-react";
import TextField from "@mui/material/TextField";
import axios from "axios";
import authHeader from "../../../Auth/authHeader";
import { ApiLink } from "../../../APILink/ApiLink";
import { getAuthData } from "../../../Auth/getAuthData";
import { PasswordInput } from "./PasswordInput";
import "./CompanyProfileTab.scss";

const toLogoFileName = (logoUrl, photoBase) => {
  if (!logoUrl) return "";
  if (photoBase && logoUrl.startsWith(photoBase)) {
    return logoUrl.slice(photoBase.length);
  }
  return logoUrl;
};

export default function CompanyProfileTab({ initialData, onUpdate }) {
  const authData = getAuthData();
  const userCompanyUnitID = Number(authData?.companyUnitId || 0);
  const isCompanyUnitUser = userCompanyUnitID > 0;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [data, setData] = useState(initialData || {});
  const [formData, setFormData] = useState(initialData || {});
  const [fieldErrors, setFieldErrors] = useState({});
  const [companyUnitDetails, setCompanyUnitDetails] = useState(null);

  useEffect(() => {
    setData(initialData || {});
    setFormData(initialData || {});
  }, [initialData]);

  useEffect(() => {
    let isMounted = true;

    const loadCompanyUnitDetails = async () => {
      if (!isCompanyUnitUser) {
        setCompanyUnitDetails(null);
        return;
      }

      try {
        const headers = authHeader();
        const response = await axios.get(ApiLink.API_URL + "CompanyUnits/getComUnitDetails", {
          headers,
          params: { id: userCompanyUnitID },
        });

        const row = Array.isArray(response?.data) ? response.data[0] : null;
        if (isMounted) {
          setCompanyUnitDetails(row || null);
        }
      } catch (error) {
        if (isMounted) {
          setCompanyUnitDetails(null);
        }
      }
    };

    loadCompanyUnitDetails();

    return () => {
      isMounted = false;
    };
  }, [isCompanyUnitUser, userCompanyUnitID]);

  const handleCancel = () => {
    setFormData({ ...data });
    setFieldErrors({});
    setIsEditing(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const nuiValue = formData.nui ?? data.nui ?? "";
    const normalizedNui = String(nuiValue).trim();
    const hasIdentifier = Boolean(normalizedNui);
    if (!hasIdentifier) {
      setFieldErrors((prev) => ({ ...prev, nui: true }));
      toast.error("Gabim validimi - Numri Identifikues (NUI) eshte i detyrueshem para ngarkimit te logos.");
      e.target.value = "";
      return;
    }

    const allowedExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
    const fileExtension = (file.name.split(".").pop() || "").toLowerCase();

    if (!allowedExtensions.includes(fileExtension) || !file.type.startsWith("image/")) {
      toast.error("Gabim validimi - Zgjidh nje file te vlefshem te fotos (jpg, jpeg, png, gif, bmp, webp).");
      e.target.value = "";
      return;
    }

    try {
      setIsUploadingLogo(true);
      const headers = authHeader();
      const fileData = new FormData();

      const safeIdentifier = normalizedNui
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

      const generatedFileName = `logo_${safeIdentifier}.${fileExtension}`;
      const renamedFile = new File([file], generatedFileName, { type: file.type });

      fileData.append("file", renamedFile, renamedFile.name);

      const response = await axios.post(ApiLink.API_URL + "Companies/SaveFile", fileData, { headers });
      const uploadedFileName = response.data;

      setFormData((prev) => ({
        ...prev,
        companyLogo: uploadedFileName,
        logoUrl: uploadedFileName ? `${ApiLink.PHOTO_URL}${uploadedFileName}` : "",
      }));

      toast.success("Sukses - Logo u ngarkua me sukses.");
    } catch (error) {
      console.error(error);
      toast.error("Gabim - Ngarkimi i logos deshtoi.");
    } finally {
      setIsUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    const nextErrors = {
      name: !formData.name,
      nui: !formData.nui,
      email: !formData.email,
    };

    if (nextErrors.name || nextErrors.nui || nextErrors.email) {
      setFieldErrors(nextErrors);
      toast.error("Gabim validimi - Emri i kompanise, Numri Identifikues (NUI) dhe email-i jane fusha te detyrueshme.");
      return;
    }

    try {
      setIsSaving(true);
      const headers = authHeader();
      
      const payload = {
        CompanyID: data.companyId || data.id,
        CompanyName: formData.name || "",
        IdentificationNumber: formData.nui || "",
        VATNumber: formData.vatNumber || "",
        Address: formData.address || "",
        CityID: formData.cityId || data.cityId || "",
        StateID: formData.stateId || data.stateId || "",
        PhoneNumbers: formData.phone || "",
        Email: formData.email || "",
        EdiPass: formData.atkPassword || "",
        ARBKPass: formData.arbkPassword || "",
        MailPass: formData.emailPassword || "",
        SMTPClient: formData.smtpClient || "",
        Comment: formData.description || "",
        ModifiedByUser: localStorage.getItem("email") || "",
        VATDeclarant: formData.vatDeclarant || data.vatDeclarant || "",
        FiscalYear: formData.fiscalYear || data.fiscalYear || "",
        IsActive: formData.isActive ?? data.isActive ?? true,
        CompanyLogo: formData.companyLogo || toLogoFileName(formData.logoUrl || data.logoUrl, ApiLink.PHOTO_URL),
      };

      await axios.put(ApiLink.API_URL + "Companies", payload, { headers });

      const updatedRecord = {
        ...data,
        ...formData,
        id: payload.CompanyID,
        companyId: payload.CompanyID,
        logoUrl: payload.CompanyLogo ? `${ApiLink.PHOTO_URL}${payload.CompanyLogo}` : "",
        companyLogo: payload.CompanyLogo || "",
      };
      
      setData({ ...updatedRecord });
      setFormData({ ...updatedRecord });
      setFieldErrors({});
      setIsEditing(false);

      if (onUpdate) {
        onUpdate({ ...updatedRecord });
      }
      
      toast.success("Sukses - Profili i kompanise u perditesua me sukses.");
    } catch (error) {
      console.error(error);
      toast.error("Gabim - Perditesimi i profilit te kompanise deshtoi.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: false }));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (!initialData) return <div className="company-profile-tab__loading">Duke ngarkuar profilin...</div>;

  return (
    <div className="company-profile-tab">
      <div className="company-profile-tab__topbar">
        <div className="company-profile-tab__heading-wrap">
          <h2 className="company-profile-tab__title">
            <Building className="company-profile-tab__title-icon" />
            Profili i Kompanise
          </h2>
          <p className="company-profile-tab__subtitle">
            Menaxho te dhenat kryesore te organizates, adresen dhe kredencialet e sistemit.
          </p>
        </div>

        {!isEditing && (
          <button
            type="button"
            onClick={() => {
              if (!isCompanyUnitUser) {
                setIsEditing(true);
              }
            }}
            className="company-profile-tab__edit-btn"
            disabled={isCompanyUnitUser}
            title={isCompanyUnitUser ? "Perdoruesi i njesise organizative nuk mund te ndryshoje profilin e kompanise." : undefined}
          >
            Ndrysho Profilin
          </button>
        )}
      </div>

      {isCompanyUnitUser && companyUnitDetails && (
        <div className="company-profile-tab__unit-card">
          <div className="company-profile-tab__unit-head">
            <h3 className="company-profile-tab__unit-title">
              <Building2 className="company-profile-tab__unit-title-icon" />
              Njesia Organizative e Perdoruesit
            </h3>
            <p className="company-profile-tab__unit-subtitle">
              Jeni i lidhur me nje njesi organizative, prandaj profili i kompanise eshte vetem per lexim.
            </p>
          </div>

          <div className="company-profile-tab__unit-grid">
            <div className="company-profile-tab__unit-item">
              <Building2 className="company-profile-tab__unit-icon" />
              <div>
                <span className="company-profile-tab__unit-label">Njesia</span>
                <strong className="company-profile-tab__unit-value">{companyUnitDetails.UnitName || "-"}</strong>
              </div>
            </div>

            <div className="company-profile-tab__unit-item">
              <Hash className="company-profile-tab__unit-icon" />
              <div>
                <span className="company-profile-tab__unit-label">NUI</span>
                <strong className="company-profile-tab__unit-value">{companyUnitDetails.IdentificationNumber || "-"}</strong>
              </div>
            </div>

            <div className="company-profile-tab__unit-item">
              <MapPin className="company-profile-tab__unit-icon" />
              <div>
                <span className="company-profile-tab__unit-label">Adresa</span>
                <strong className="company-profile-tab__unit-value">{companyUnitDetails.UnitAdress || "-"}</strong>
              </div>
            </div>

            <div className="company-profile-tab__unit-item">
              <Phone className="company-profile-tab__unit-icon" />
              <div>
                <span className="company-profile-tab__unit-label">Telefoni</span>
                <strong className="company-profile-tab__unit-value">{companyUnitDetails.PhoneNumbers || "-"}</strong>
              </div>
            </div>

            <div className="company-profile-tab__unit-item company-profile-tab__unit-item--full">
              <Mail className="company-profile-tab__unit-icon" />
              <div>
                <span className="company-profile-tab__unit-label">Email</span>
                <strong className="company-profile-tab__unit-value">{companyUnitDetails.Email || "-"}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="company-profile-tab__layout">
        <div className="company-profile-tab__left-column">
          <div className="company-profile-tab__logo-card">
            <div className="company-profile-tab__logo-wrap">
              {formData.logoUrl ? (
                <img src={formData.logoUrl} alt="Logo e Kompanise" className="company-profile-tab__logo-image" />
              ) : (
                <Building className="company-profile-tab__logo-placeholder" />
              )}
            </div>
            
            {isEditing && (
              <div className="company-profile-tab__upload-wrap">
                <label htmlFor="companyLogoUpload" className="company-profile-tab__upload-label">
                  <ImagePlus className="company-profile-tab__upload-icon" />
                  Ngarko Logo (Attachment)
                </label>
                <input
                  id="companyLogoUpload"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/bmp,image/webp"
                  onChange={handleLogoUpload}
                  className="company-profile-tab__file-input"
                  disabled={isUploadingLogo}
                />
                <p className="company-profile-tab__upload-note">
                  {isUploadingLogo
                    ? "Duke ngarkuar logon..."
                    : "Formatet e lejuara: JPG, PNG, GIF, BMP, WEBP. Emri gjenerohet automatikisht sipas NUI."}
                </p>
              </div>
            )}
            
            <div className="company-profile-tab__company-brief">
              <h3 className="company-profile-tab__company-name">{formData.name || 'Kompania Juaj'}</h3>
              <p className="company-profile-tab__company-vat">{formData.vatNumber ? `TVSH: ${formData.vatNumber}` : 'TVSH nuk eshte vendosur'}</p>
            </div>
          </div>
          
          <div className="company-profile-tab__note-card">
            <div className="company-profile-tab__note-row">
              <Info className="company-profile-tab__note-icon" />
              <p className="company-profile-tab__note-text">
                <span className="company-profile-tab__note-strong">Shenim sigurie:</span> Fusha "Fjalekalimi i Email-it" i referohet App Password-it te gjeneruar nga email provider-i. Fjalekalimet e sistemit ruhen te enkriptuara dhe vetem administratoret e caktuar duhet te kene qasje per t'i ndryshuar.
              </p>
            </div>
          </div>
        </div>

        <div className="company-profile-tab__right-column">
          <div className="company-profile-tab__section-card">
            <div className="company-profile-tab__section-head">
              <h3 className="company-profile-tab__section-title">
                <Info className="company-profile-tab__section-icon" />
                Informata te Pergjithshme
              </h3>
            </div>
              <div className="company-profile-tab__section-body company-profile-tab__section-body--general">
              <div className="company-profile-tab__grid company-profile-tab__priority-grid">
              <div className="company-profile-tab__field company-profile-tab__field--card">
                  <TextField
                  id="name"
                    label="Emri i Kompanise *"
                    variant="filled"
                    fullWidth
                  name="name"
                    placeholder="Shkruaj emrin e kompanise"
                  value={formData.name || ''}
                  onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                    error={Boolean(fieldErrors.name)}
                    helperText={fieldErrors.name ? "Emri i kompanise eshte i detyrueshem" : ""}
                />
              </div>

              <div className="company-profile-tab__field company-profile-tab__field--card">
                  <TextField
                  id="nui"
                    label="Numri Identifikues (NUI)"
                    variant="filled"
                    fullWidth
                  name="nui"
                    placeholder="Shkruaj numrin identifikues"
                  value={formData.nui || ''}
                  onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                    error={Boolean(fieldErrors.nui)}
                    helperText={fieldErrors.nui ? "Numri Identifikues (NUI) eshte i detyrueshem" : ""}
                />
              </div>

              <div className="company-profile-tab__field company-profile-tab__field--card">
                  <TextField
                  id="vatNumber"
                    label="Numri TVSH"
                    variant="filled"
                    fullWidth
                  name="vatNumber"
                    placeholder="Shkruaj numrin e TVSH-se"
                  value={formData.vatNumber || ''}
                  onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                  />
                </div>

                <div className="company-profile-tab__field company-profile-tab__field--span-2 company-profile-tab__field--card">
                  <TextField
                    id="description"
                    label="Pershkrimi"
                    variant="filled"
                    fullWidth
                    multiline
                    minRows={3}
                    name="description"
                    placeholder="Pershkrim i shkurter i kompanise"
                    value={formData.description || ''}
                    onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                />
              </div>
            </div>
              </div>
          </div>

          <div className="company-profile-tab__section-card">
            <div className="company-profile-tab__section-head">
              <h3 className="company-profile-tab__section-title">
                <MapPin className="company-profile-tab__section-icon" />
                Kontakti dhe Adresa
              </h3>
            </div>
              <div className="company-profile-tab__section-body company-profile-tab__section-body--contact">
              <div className="company-profile-tab__grid company-profile-tab__contact-grid">
                <div className="company-profile-tab__field company-profile-tab__field--card">
                  <TextField
                    id="email"
                    label="Email Kryesor *"
                    variant="filled"
                    fullWidth
                    name="email"
                    type="email"
                    placeholder="email@kompania.com"
                    value={formData.email || ''}
                    onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                    error={Boolean(fieldErrors.email)}
                    helperText={fieldErrors.email ? "Email-i eshte i detyrueshem" : ""}
                  />
                </div>

                <div className="company-profile-tab__field company-profile-tab__field--card">
                  <TextField
                    id="phone"
                    label="Numri i Telefonit"
                    variant="filled"
                    fullWidth
                    name="phone"
                    placeholder="044 123 456"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                  />
                </div>

                <div className="company-profile-tab__field company-profile-tab__field--card">
                  <TextField
                    id="country"
                    label="Shteti"
                    variant="filled"
                    fullWidth
                    name="country"
                    placeholder="Kosove"
                    value={formData.country || ''}
                    onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                  />
                </div>

                <div className="company-profile-tab__field company-profile-tab__field--card">
                  <TextField
                    id="city"
                    label="Qyteti"
                    variant="filled"
                    fullWidth
                    name="city"
                    placeholder="Prishtine"
                    value={formData.city || ''}
                    onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                  />
                </div>

                <div className="company-profile-tab__field company-profile-tab__field--span-2 company-profile-tab__field--card">
                  <TextField
                    id="address"
                    label="Adresa e Detajuar"
                    variant="filled"
                    fullWidth
                    name="address"
                    placeholder="Rr. Shembull, Nr. 10"
                    value={formData.address || ''}
                    onChange={handleChange}
                    InputProps={{ readOnly: !isEditing }}
                  />
              </div>
            </div>
              </div>
          </div>

          <div className="company-profile-tab__section-card">
            <div className="company-profile-tab__section-head company-profile-tab__section-head--warning">
              <h3 className="company-profile-tab__section-title">
                <Save className="company-profile-tab__section-icon company-profile-tab__section-icon--warning" />
                Kredencialet e Sistemit
              </h3>
            </div>
            <div className="company-profile-tab__section-body company-profile-tab__section-body--credentials">
            <div className="company-profile-tab__grid company-profile-tab__credentials-grid">
              <div className="company-profile-tab__field company-profile-tab__field--card">
                <PasswordInput 
                  label="Fjalekalimi i Email-it (App Password)" 
                  name="emailPassword" 
                  value={formData.emailPassword || ''} 
                  onChange={handleChange}
                  InputProps={{ readOnly: !isEditing }}
                  helperText={isEditing ? "Vendos App Password-in e gjeneruar nga emaili" : ""}
                />
              </div>
              <div className="company-profile-tab__field company-profile-tab__field--card">
                <PasswordInput 
                  label="Fjalekalimi i Sistemit ATK" 
                  name="atkPassword" 
                  value={formData.atkPassword || ''} 
                  onChange={handleChange}
                  InputProps={{ readOnly: !isEditing }}
                />
              </div>
              <div className="company-profile-tab__field company-profile-tab__field--card">
                <PasswordInput 
                  label="Fjalekalimi ARBK" 
                  name="arbkPassword" 
                  value={formData.arbkPassword || ''} 
                  onChange={handleChange}
                  InputProps={{ readOnly: !isEditing }}
                />
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="company-profile-tab__sticky-actions">
          <button type="button" onClick={handleCancel} disabled={isSaving} className="company-profile-tab__cancel-btn">
            <X className="mr-2 h-4 w-4" /> Anulo Ndryshimet
          </button>
          <button type="button" onClick={handleSave} disabled={isSaving} className="company-profile-tab__save-btn">
            {isSaving ? "Duke ruajtur..." : <><Save className="mr-2 h-4 w-4" /> Ruaj Ndryshimet</>}
          </button>
        </div>
      )}
    </div>
  );
}